// src/pages/actividades/ActividadDetallePage.tsx
import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { Card, Button, Badge, StatusBadge, PriorityBadge, PageLoader } from '@/components/ui';
import { ArrowLeft, Edit, Check, RotateCcw, Clock, Calendar, User, Building, FileText, Repeat, CheckCircle, Paperclip } from 'lucide-react';
import { formatDate, formatDateTime } from '@/lib/utils';
import { useActividad, useSubtareas, useCompletarActividad, useReabrirActividad } from '@/hooks/queries/useActividades';
import { describirPatron } from '@/lib/recurrencia';
import { SubtareasList } from './components/SubtareasList';
import { SubtareaInlineAdd } from './components/SubtareaInlineAdd';
import { ActividadDrawer } from './components/ActividadDrawer';
import { TiempoPanel } from './components/TiempoPanel';
import { HistorialTimeline } from './components/HistorialTimeline';
import { AdjuntosPanel } from '@/components/common';

export function ActividadDetallePage() {
  const { id } = useParams<{ id: string }>();
  const { data: actividad, isLoading } = useActividad(id!);
  const { data: subtareas } = useSubtareas(id!);
  const completar = useCompletarActividad();
  const reabrirMutation = useReabrirActividad();

  const [editModalOpen, setEditModalOpen] = useState(false);

  if (isLoading) return <PageLoader message="Cargando actividad..." />;
  if (!actividad) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Actividad no encontrada</p>
        <Link to="/actividades" className="text-blue-600 hover:underline mt-2 inline-block">
          Volver a actividades
        </Link>
      </div>
    );
  }

  const handleCompletar = async () => {
    const completarSubs =
      actividad.subtareas_count && actividad.tiene_subtareas_pendientes
        ? window.confirm('Hay subtareas pendientes. ¿Desea completarlas tambien?')
        : false;
    await completar.mutateAsync({ id: actividad.id, completarSubtareas: completarSubs });
  };

  const isCompletable = actividad.estado !== 'COMPLETADA' && actividad.estado !== 'CANCELADA';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Link to="/actividades" className="text-gray-500 hover:text-gray-700 mt-1">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="text-2xl font-bold">{actividad.titulo}</h1>
              <StatusBadge status={actividad.estado} />
              <PriorityBadge priority={actividad.prioridad} />
            </div>
            <p className="text-gray-500">
              Creada {formatDateTime(actividad.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isCompletable && (
            <Button onClick={handleCompletar} loading={completar.isPending}>
              <Check className="h-4 w-4 mr-2" />
              Completar
            </Button>
          )}
          {actividad.estado === 'COMPLETADA' && (
            <Button
              variant="outline"
              onClick={() => reabrirMutation.mutate(actividad.id)}
              loading={reabrirMutation.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reabrir
            </Button>
          )}
          <Button variant="outline" onClick={() => setEditModalOpen(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      {/* Recurrence banner for instances */}
      {actividad.actividad_plantilla_id && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-sm">
          <Repeat className="h-4 w-4 text-blue-500 shrink-0" />
          <span className="text-blue-700 dark:text-blue-300">
            Instancia recurrente
            {actividad.es_recurrente && ` · ${describirPatron(actividad)}`}
          </span>
          <Link
            to={`/actividades/${actividad.actividad_plantilla_id}`}
            className="ml-auto text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-medium"
          >
            Editar recurrencia →
          </Link>
        </div>
      )}

      {/* Plantilla banner */}
      {actividad.es_plantilla && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-sm">
          <Repeat className="h-4 w-4 text-purple-500 shrink-0" />
          <span className="text-purple-700 dark:text-purple-300">
            Plantilla recurrente · {describirPatron(actividad)}
            {actividad.proxima_ocurrencia && ` · Próxima: ${formatDate(actividad.proxima_ocurrencia)}`}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contenido principal */}
        <div className="lg:col-span-2 space-y-4">
          {/* Descripcion */}
          <Card className="p-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Descripcion
            </h3>
            <p className="text-gray-700 whitespace-pre-wrap">
              {actividad.descripcion || 'Sin descripcion'}
            </p>
          </Card>

          {/* Subtareas */}
          <Card className="p-4">
            <h3 className="font-semibold mb-4">
              Subtareas ({subtareas?.length ?? 0})
            </h3>
            <SubtareasList subtareas={subtareas ?? []} />
            <div className="mt-2">
              <SubtareaInlineAdd actividadPadreId={id!} />
            </div>
          </Card>

          {/* Adjuntos */}
          <Card className="p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Adjuntos
            </h3>
            <AdjuntosPanel contexto="ACTIVIDAD" entidadId={id!} />
          </Card>
        </div>

        {/* Panel lateral */}
        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Detalles</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge variant="default">{actividad.area}</Badge>
                {actividad.tipo_actividad && (
                  <Badge variant="primary">{typeof actividad.tipo_actividad === 'string' ? actividad.tipo_actividad : actividad.tipo_actividad.nombre}</Badge>
                )}
              </div>

              {actividad.responsable && (
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  <span>
                    {actividad.responsable.nombre} {actividad.responsable.apellido}
                  </span>
                </div>
              )}

              {actividad.cliente && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Building className="h-4 w-4" />
                  <Link
                    to={`/clientes/${actividad.cliente.id}`}
                    className="hover:text-blue-600"
                  >
                    {actividad.cliente.nombre}
                  </Link>
                </div>
              )}

              {actividad.fecha_agendada && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Agendada: {formatDate(actividad.fecha_agendada)}
                    {actividad.hora_agendada && ` a las ${actividad.hora_agendada}`}
                  </span>
                </div>
              )}

              {actividad.fecha_limite && (
                <div className="flex items-center gap-2 text-orange-600">
                  <Clock className="h-4 w-4" />
                  <span>Limite: {formatDate(actividad.fecha_limite)}</span>
                </div>
              )}

              {actividad.duracion_estimada_min && actividad.duracion_estimada_min > 0 && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>Estimado: {actividad.duracion_estimada_min} min</span>
                </div>
              )}

              {actividad.completada_at && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span>Completada: {formatDateTime(actividad.completada_at)}</span>
                </div>
              )}

              {actividad.tiempo_registrado_min > 0 && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>Registrado: {actividad.tiempo_registrado_min} min</span>
                </div>
              )}

              {actividad.ticket && (
                <div className="pt-2 border-t">
                  <span className="text-gray-500">Ticket relacionado:</span>
                  <Link
                    to={`/tickets/${actividad.ticket.id}`}
                    className="text-blue-600 hover:underline ml-1"
                  >
                    #{actividad.ticket.numero} - {actividad.ticket.titulo}
                  </Link>
                </div>
              )}
            </div>
          </Card>

          {/* Control de Tiempo */}
          <TiempoPanel
            actividadId={actividad.id}
            tiempoRegistradoMin={actividad.tiempo_registrado_min ?? 0}
          />

          {/* Historial de cambios */}
          <HistorialTimeline actividadId={actividad.id} />

          {/* Info adicional */}
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Informacion</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Nivel</span>
                <span className="font-medium">{actividad.nivel}</span>
              </div>
              <div className="flex justify-between">
                <span>Personal</span>
                <span className="font-medium">{actividad.es_personal ? 'Si' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span>Recurrente</span>
                <span className="font-medium">
                  {actividad.es_recurrente ? describirPatron(actividad) || 'Sí' : 'No'}
                </span>
              </div>
              {actividad.updated_at && (
                <div className="pt-2 border-t text-xs text-gray-400">
                  Actualizada: {formatDateTime(actividad.updated_at)}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <ActividadDrawer
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        actividad={actividad}
      />
    </div>
  );
}
